import { getSupabase } from '@/lib/supabase';
import { api, firstValidationError, withStatus, type ApiResponse } from '@/lib/apiClient';
import type { TemplateSnapshot } from '@/types/nutrition';
import type { CreateAssignmentBody, UpdateAssignmentBody } from '@schemas/nutrition';

export interface AssignmentInsert {
  clientId: string;
  startDate: string; // "YYYY-MM-DD"
  timezone: string;
  title?: string;
  notes?: string;
  templateSnapshot: TemplateSnapshot;
}

function validateUuid(value: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}

function validateDateString(value: string): boolean {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(value)) return false;
  const date = new Date(value + 'T00:00:00');
  return !isNaN(date.getTime());
}

function validateAssignmentInsert(input: any): AssignmentInsert {
  if (!input || typeof input !== 'object') {
    throw new Error('Invalid input: must be an object');
  }

  if (!input.clientId || typeof input.clientId !== 'string' || !validateUuid(input.clientId)) {
    throw new Error('Invalid clientId: must be a valid UUID string');
  }

  if (!input.startDate || typeof input.startDate !== 'string' || !validateDateString(input.startDate)) {
    throw new Error('Invalid startDate: must be a valid YYYY-MM-DD string');
  }

  if (!input.timezone || typeof input.timezone !== 'string') {
    throw new Error('Invalid timezone: must be a string');
  }

  if (input.title !== undefined && typeof input.title !== 'string') {
    throw new Error('Invalid title: must be a string or undefined');
  }

  if (input.notes !== undefined && typeof input.notes !== 'string') {
    throw new Error('Invalid notes: must be a string or undefined');
  }

  if (!input.templateSnapshot || typeof input.templateSnapshot !== 'object') {
    throw new Error('Invalid templateSnapshot: must be an object');
  }

  return input as AssignmentInsert;
}

export async function createAssignment(input: AssignmentInsert) {
  const parsed = validateAssignmentInsert(input);
  const supabase = getSupabase();
  if (!supabase) throw new Error('Supabase not configured');

  const {
    data: { user },
    error: userErr,
  } = await supabase.auth.getUser();
  if (userErr || !user) throw new Error('Not authenticated');

  const payload = {
    coach_id: user.id,
    client_id: parsed.clientId,
    start_date: parsed.startDate,
    timezone: parsed.timezone,
    title: parsed.title ?? null,
    notes: parsed.notes ?? null,
    template_snapshot: parsed.templateSnapshot,
  };

  const { data, error } = await supabase.from('client_nutrition_assignments').insert(payload).select().single();

  if (error) throw error;

  // Client events are now handled automatically by database triggers

  return data;
}

export async function replaceClientAssignment(input: any) {
  // Use Worker endpoint (BFF pattern for httpOnly cookie auth)
  const res = await api.post<unknown>('/api/coach/nutrition-assignments', {
    clientId: input.clientId,
    // Optional but NOT nullable server-side: omit when absent (null would 400). See tests/contracts.
    ...(input.templateId ? { templateId: input.templateId } : {}),
    ...(input.title ? { title: input.title } : {}),
    startDate: input.startDate,
    timezone: input.timezone ?? 'UTC',
    notes: input.notes ?? null,
    editorData: input.editorData, // Direct editor format only
    pdfObjectPath: input.pdfObjectPath ?? null,
  } satisfies CreateAssignmentBody);

  if (!res.ok) {
    console.error('Failed to create nutrition assignment from Worker:', res.status, res.body);
    const body = res.body as { message?: string; error?: string } | undefined;
    throw new Error(
      firstValidationError(body) ||
        body?.message ||
        body?.error ||
        withStatus(res, (status) => `Failed to create assignment (${status})`),
    );
  }

  return (res as { assignment?: unknown }).assignment;
}

export async function listAssignmentsForCoach() {
  // Use Worker endpoint (BFF pattern for httpOnly cookie auth)
  try {
    const res = await fetch('/api/coach/nutrition-assignments', {
      credentials: 'include',
    });

    if (!res.ok) {
      console.error('Failed to list nutrition assignments from Worker:', res.status);
      throw new Error('Not authenticated');
    }

    const { items } = await res.json();
    return items || [];
  } catch (error) {
    console.error('Failed to list nutrition assignments:', error);
    throw error;
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export async function getActiveAssignmentForClient(clientId: string, onDateISO: string) {
  if (!UUID_RE.test(clientId)) {
    throw new Error(`Bad clientId passed to getActiveAssignmentForClient: ${clientId}`);
  }

  const supabase = getSupabase();
  if (!supabase) throw new Error('Supabase not configured');

  // simplest rule: latest assignment whose start_date <= onDate
  const { data, error } = await supabase
    .from('client_nutrition_assignments')
    .select('*')
    .eq('client_id', clientId)
    .lte('start_date', onDateISO)
    .order('start_date', { ascending: false })
    .limit(1);

  if (error) throw error;
  return data?.[0] ?? null;
}

// Helper to pick the day from the snapshot by date with timezone support
export function selectSnapshotDay(
  template: TemplateSnapshot,
  startDateISO: string,
  currentDateISO: string,
  timezone?: string,
) {
  const days = template.days ?? [];
  if (!days.length) return null;

  // If timezone is provided, convert dates to that timezone for calculation
  let startDate: Date;
  let currentDate: Date;

  if (timezone && timezone !== 'UTC') {
    // For timezone-aware calculation, we need to consider the timezone offset
    // This is a simplified approach - for production, consider using a proper timezone library
    startDate = new Date(startDateISO + 'T00:00:00');
    currentDate = new Date(currentDateISO + 'T00:00:00');
  } else {
    startDate = new Date(startDateISO + 'T00:00:00');
    currentDate = new Date(currentDateISO + 'T00:00:00');
  }

  const diffMs = currentDate.getTime() - startDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const idx = ((diffDays % days.length) + days.length) % days.length; // safe modulo
  return days[idx];
}

// Helper to pick the day type from DayType[] by date with timezone support
export function selectDayType(
  dayTypes: any[],
  startDateISO: string,
  currentDateISO: string,
  timezone?: string,
): any | null {
  if (!dayTypes || dayTypes.length === 0) return null;

  // Calculate day offset based on start date and current date
  let startDate: Date;
  let currentDate: Date;

  if (timezone && timezone !== 'UTC') {
    startDate = new Date(startDateISO + 'T00:00:00');
    currentDate = new Date(currentDateISO + 'T00:00:00');
  } else {
    startDate = new Date(startDateISO + 'T00:00:00');
    currentDate = new Date(currentDateISO + 'T00:00:00');
  }

  const diffMs = currentDate.getTime() - startDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const idx = ((diffDays % dayTypes.length) + dayTypes.length) % dayTypes.length;
  return dayTypes[idx];
}

// Helper to get current date in assignment timezone
export function getCurrentDateInTimezone(timezone: string): string {
  const now = new Date();

  // For UTC or if timezone handling is not critical, use simple approach
  if (timezone === 'UTC' || !timezone) {
    return now.toISOString().split('T')[0];
  }

  // For other timezones, use toLocaleDateString with timezone
  // This is a simplified approach - for production, consider using a proper timezone library
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return formatter.format(now); // Returns YYYY-MM-DD format
  } catch (_error) {
    console.warn(`Invalid timezone ${timezone}, falling back to UTC`);
    return now.toISOString().split('T')[0];
  }
}

// Client customization functions
export async function saveClientPlanSnapshot(assignmentId: string, editorData: any) {
  // Use Worker endpoint (BFF pattern for httpOnly cookie auth).
  // The PUT handler enforces requireCsrf; apiClient attaches the token (this call once 403'd without it).
  const res = await api.put<unknown>(`/api/coach/nutrition-assignments/${assignmentId}`, {
    workingData: editorData, // Direct editor format only
    isCustomized: true,
  } satisfies UpdateAssignmentBody);

  if (!res.ok) {
    console.error('[saveClientPlanSnapshot] Error response:', res.status, res.body);
    // Legacy copy: only a body-level `error` is surfaced (this handler sends `message`, so users
    // see the generic text).
    throw new Error((res.body as { error?: string } | undefined)?.error || 'Failed to save snapshot');
  }

  return (res as { assignment?: unknown }).assignment;
}

// Use the view so callers always get the "effective" plan
export async function getEffectivePlanForClient(clientId: string, onDateISO: string) {
  // Use Worker endpoint (BFF pattern for httpOnly cookie auth)
  try {
    const res = await fetch(
      `/api/clients/nutrition-plan?clientId=${encodeURIComponent(clientId)}&date=${encodeURIComponent(onDateISO)}`,
      { credentials: 'include' },
    );

    if (!res.ok) {
      console.error('Failed to fetch effective plan from Worker:', res.status);
      return null;
    }

    const { plan } = await res.json();
    return plan; // { effective_snapshot, ... }
  } catch (error) {
    console.error('Failed to fetch effective plan:', error);
    return null;
  }
}

export async function revertClientCustomizations(assignmentId: string) {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { data, error } = await sb
    .from('client_nutrition_assignments')
    .update({
      working_snapshot: null,
      is_customized: false,
      customized_at: null,
    })
    .eq('id', assignmentId)
    .select()
    .single();
  if (error) throw error;

  // Client events are now handled automatically by database triggers

  return data;
}

/** Remove a client's nutrition plan assignment via the Worker. Non-throwing. */
export function deleteAssignment(planId: string): Promise<ApiResponse> {
  return api.delete(`/api/coach/nutrition-assignments?id=${encodeURIComponent(planId)}`);
}
